"use client"
import { zodResolver } from "@hookform/resolvers/zod"
import {
  FlexColumn,
  Separator,
  SideSheetBody,
  SideSheetHeader,
  SideSheetHeaderTitle,
  TextLabel,
  UnstyledButton,
} from "@opensea/ui-kit"
import { ExpandMore } from "@opensea/ui-kit/icons"
import { bn } from "@opensea/utils"
import { useEffect, useState } from "react"
import { FormProvider, useForm, useFormState, useWatch } from "react-hook-form"
import { useTranslations } from "use-intl"
import { Button } from "../../../components/Button"
import { WalletAddressInput } from "../../../components/WalletAddressInput"
import { useAddressInputField } from "../../../hooks/useAddressInput"
import { useChainId } from "../../../polychain/hooks/useChainId"
import { FlowActions } from "../../../stores/flowStore"
import type { SendFlowState } from "../../flow-state"
import { Sending } from "./components/Sending"
import { SendReview } from "./components/SendReview"
import { TokenQuantityInput } from "./components/TokenQuantityInput"
import type { TransferTokensActionsResponse } from "./hooks/useTransferTokens"
import { useTransferTokens } from "./hooks/useTransferTokens"
import { useSendQuote } from "./provider/SendProvider"
import type { SendFlowFormValues } from "./schema"
import { sendFlowFormSchema } from "./schema"

type SendFlowContentProps = {
  onBack: (() => void) | undefined
}

export const SendFlowContent = ({ onBack }: SendFlowContentProps) => {
  const t = useTranslations("wallet.SendFlow")
  const chainId = useChainId()

  const [container, setContainer] = useState<HTMLElement | null>(null)
  const [actions, setActions] = useState<TransferTokensActionsResponse>([])

  const { currency } = useSendQuote()

  const form = useForm<SendFlowFormValues>({
    mode: "onChange",
    defaultValues: {
      quantity: undefined,
      recipient: {
        identifier: "",
        resolvedAddress: undefined,
        isResolving: false,
      },
    },
    resolver: zodResolver(sendFlowFormSchema),
  })

  const { handleSubmit, control } = form
  const { isValid } = useFormState({ control })

  const quantity = useWatch({ control, name: "quantity" })
  const recipient = useWatch({ control, name: "recipient" })
  const quantityUsd = quantity
    ? bn(quantity).times(currency.usdPrice).toString()
    : ""

  const addressInputProps = useAddressInputField({
    field: "recipient",
    setValue: form.setValue,
    identifier: recipient.identifier,
  })

  useEffect(() => {
    FlowActions.setFlowField<SendFlowState>("currency", currency)
    FlowActions.setFlowField<SendFlowState>("amount", quantity)
  }, [currency, quantity])

  const transferTokens = useTransferTokens()

  const onSubmit = handleSubmit(async data => {
    if (!data.recipient.resolvedAddress) {
      throw new Error("No profile address")
    }

    const actions = await transferTokens({
      chain: currency.chainIdentifier,
      contractAddress: currency.contractAddress,
      quantity: data.quantity,
      toAddress: data.recipient.resolvedAddress,
    })

    if (!actions) {
      throw new Error("No actions")
    }

    setActions(actions)
  })

  if (!chainId) {
    FlowActions.endFlow() // End flow if no chainId (no wallet connected)
  }

  if (actions.length > 0 && addressInputProps.resolvedAddress) {
    return (
      <Sending
        actions={actions}
        address={addressInputProps.resolvedAddress}
        currency={currency}
        ensName={addressInputProps.ensName}
        imageUrl={addressInputProps.imageUrl}
        onBack={() => {
          setActions([])
        }}
        quantity={quantity}
      />
    )
  }

  return (
    <FormProvider {...form}>
      <SideSheetHeader className="gap-4">
        {onBack && (
          <UnstyledButton onClick={onBack}>
            <ExpandMore className="rotate-90" size={24} title={t("back")} />
          </UnstyledButton>
        )}
        <SideSheetHeaderTitle>{t("title")}</SideSheetHeaderTitle>
      </SideSheetHeader>
      <Separator />
      <SideSheetBody
        className="flex grow flex-col justify-between gap-10 p-6"
        ref={setContainer}
      >
        <FlexColumn className="gap-10">
          <FlexColumn className="relative gap-4">
            <TextLabel color="text-secondary">
              {t("tokenSelectedLabel")}
            </TextLabel>
            <TokenQuantityInput container={container} />
          </FlexColumn>

          <FlexColumn className="relative gap-4">
            <TextLabel color="text-secondary">
              {t("recipientWalletLabel")}
            </TextLabel>
            <WalletAddressInput
              setValue={value => form.setValue("recipient.identifier", value)}
              {...addressInputProps}
              {...form.register("recipient.identifier")}
            />
          </FlexColumn>
        </FlexColumn>

        <FlexColumn className="gap-6">
          <SendReview
            currency={currency}
            priceUSD={currency.usdPrice}
            quantity={quantity}
            quantityUsd={quantityUsd}
            total={quantityUsd}
          />
          <Button
            className="font-mono"
            disabled={!(isValid && recipient.resolvedAddress)}
            onClick={onSubmit}
          >
            {t("sendButton")}{" "}
            <span className="font-normal">
              {quantity} {currency.symbol}
            </span>
          </Button>
        </FlexColumn>
      </SideSheetBody>
    </FormProvider>
  )
}
